import * as React from "react";
import { observer, inject } from "mobx-react";
import { useNavigate } from "react-router-dom";


import { AppStore } from "Stores/appStore";
import { CommonStore } from "Stores/commonStore";
import { DashboardStore } from "Stores/dashboardStore";
import { LabelStore } from "Stores/labelStore";
import { ReportStore } from "Stores/reportStore";
import { TicketStore } from "Stores/dashboardStore/tickets";
import { UserStore } from "Stores/userStore";
import { StrategicPlanStore } from "Stores/strategicPlanStore";

import { TicketSelector } from "./TicketSelector";

import { Button, Form, Col, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";

import { Utils } from "../../common/utils";

import * as Sentry from "@sentry/react";

import { CreatableWrapper, SelectWrapper } from "Components/Common/SelectWrapper";
import {SimpleCheckbox} from "Components/Common/SimpleCheckbox";

export interface ActionPlanSearchFilters {
    createdStartFilter?: string;
    createdEndFilter?: string;
    evaluationStartFilter?: string;
    evaluationEndFilter?: string;
    statusFilter?: string;
    studentFilter?: string;
    courseFilter?: string;
    committeeFilter?: string;
    labelsFilter?: string;
    queryFilter?: string;
    assigneeFilter?: string;
    creatorFilter?: string;
    dashboardFilter?: string;
    strategicPlanFilter?: string;
    includeObservations?: boolean;
    includeOpenTasks?: boolean;
    includeClosedGoalMetTasks?: boolean;
    includeClosedGoalNotMetTasks?: boolean;
}

const TODAY: Date = new Date(Date.now());
interface ITicketSearchFormModalState {
    dateCreatedFrom?: Date | undefined | null
    dateCreatedTo?: Date | undefined | null
    dueDateFrom?: Date | undefined | null
    dueDateTo?: Date | undefined | null

    includeOpenTasks: boolean
    includeClosedGoalMetTasks: boolean
    includeClosedGoalNotMetTasks: boolean
    includeObservations: boolean

    strategicPlanItems: any[]
    courses: any[]
    committees: any[]
    students: any[]
    assignees: any[]
    creators: any[]
    labels: any[]
    dashboards: any[]
    taskSummaryKeywords: any[]
    redirectToTickets?: boolean | undefined
    currentFilters?: ActionPlanSearchFilters
}

interface ITicketSearchFormModalProps {
    commonStore?: CommonStore
    dashboardStore?: DashboardStore
    labelStore?: LabelStore
    userStore?: UserStore
    appStore?: AppStore
    ticketStore?: TicketStore
    reportStore?: ReportStore
    strategicPlanStore?: StrategicPlanStore
    navigate?: any
}


@inject("commonStore", "dashboardStore", "labelStore", "userStore", "appStore", "ticketStore", "reportStore", "strategicPlanStore")
@observer
class TicketSearchFormClass extends React.Component<ITicketSearchFormModalProps, ITicketSearchFormModalState> {

    onNewSearch = () => {};

    state = {
        dateCreatedFrom: undefined,
        dateCreatedTo: undefined,
        dueDateFrom: undefined,
        dueDateTo: undefined,
        includeOpenTasks: true,
        includeClosedGoalMetTasks: true,
        includeClosedGoalNotMetTasks: true,
        includeObservations: true,
        strategicPlanItems: [],
        courses: [],
        committees: [],
        students: [],
        assignees: [],
        labels: [],
        dashboards: [],
        creators: [],
        taskSummaryKeywords: [],
        redirectToTickets: undefined,
        currentFilters: undefined,
    };

    componentDidMount(){
        Utils.setViewName("ReportTicketSearch", "Create Report");
        if(this.props.commonStore && this.props.dashboardStore && this.props.labelStore && this.props.userStore && this.props.strategicPlanStore){
            this.props.commonStore.fetchCommittees();
            this.props.commonStore.fetchCourses("basic");
            this.props.commonStore.fetchStudents();
            this.props.dashboardStore.getDashboardsInCategories();
            this.props.labelStore.fetchLabels();
            this.props.userStore.fetchUserList();
            this.props.strategicPlanStore.fetchStrategicPlanItemsOnly();
        }

        if(this.props.ticketStore && !this.props.ticketStore.allTickets){
            this.props.ticketStore.fetchTickets(false);
        }
    }

    render(){
        const {commonStore, dashboardStore, labelStore, userStore, reportStore, ticketStore, strategicPlanStore} = this.props;

        if(!commonStore || !dashboardStore || !labelStore || !userStore || !reportStore || !ticketStore || !strategicPlanStore){
            Sentry.addBreadcrumb({
                message: "Store in TicketSearchForm not loaded",
                data: {commonStore, dashboardStore, labelStore, userStore, reportStore, ticketStore, strategicPlanStore},
            });
            return null;
        }

        if(this.state.redirectToTickets) {
            this.props.navigate(`${Utils.getLinkBase()}/reports`);
            return null;
        }

        return this.renderForm();
    }

    validateCreateDate = () => {
        if(!this.state.dateCreatedFrom || !this.state.dateCreatedTo) return;
        if(this.state.dateCreatedFrom! > this.state.dateCreatedTo!){
            this.setState({
                dateCreatedFrom: undefined,
                dateCreatedTo: undefined,
            });
            if(this.props.appStore){
                this.props.appStore.addError(new Error(), "Invalid Date Created Selection");
            }
        }
    };

    validateDueDate = () => {
        if(!this.state.dueDateFrom || !this.state.dueDateTo) return;
        if(this.state.dueDateFrom! > this.state.dueDateTo!){
            this.setState({
                dueDateFrom: undefined,
                dueDateTo: undefined,
            });
            if(this.props.appStore){
                this.props.appStore.addError(new Error(), "Invalid Due Date Selection");
            }
        }
    };

    handleSummaryKeyword = (o: any[]) => {
        this.setState({taskSummaryKeywords: o || []});
    };

    getValue = (obj: any) => obj.value;

    handleTicketSearch = () => {
        /*
         * Build filters for backend API instead of frontend filtering
         */
        // Extract state properties for filter building
        const {
            includeObservations, includeOpenTasks, includeClosedGoalMetTasks, includeClosedGoalNotMetTasks,
            courses, committees, students, assignees, creators, labels, dashboards, taskSummaryKeywords, strategicPlanItems
        } = this.state;
        const dateCreatedFrom = this.state.dateCreatedFrom as Date | undefined | null;
        const dateCreatedTo = this.state.dateCreatedTo as Date | undefined | null;
        const dueDateFrom = this.state.dueDateFrom as Date | undefined | null;
        const dueDateTo = this.state.dueDateTo as Date | undefined | null;

        // Build status filter based on included task types
        let statusFilter = "";
        const statuses: string[] = [];
        if (includeOpenTasks) statuses.push("open");
        if (includeClosedGoalMetTasks) statuses.push("goal-met");
        if (includeClosedGoalNotMetTasks) statuses.push("goal-not-met");
        // Add observations as a status if needed (backend may handle this differently)
        if (includeObservations) statuses.push("observation");
        if (statuses.length > 0) {
            statusFilter = statuses.join(",");
        }

        // Build filters object for backend API
        const filters: ActionPlanSearchFilters = {
            // Date filters
            createdStartFilter: dateCreatedFrom && dateCreatedFrom instanceof Date ? Utils.formatDate(dateCreatedFrom, "YYYY-MM-DD") : undefined,
            createdEndFilter: dateCreatedTo && dateCreatedTo instanceof Date ? Utils.formatDate(dateCreatedTo, "YYYY-MM-DD") : undefined,
            evaluationStartFilter: dueDateFrom && dueDateFrom instanceof Date ? Utils.formatDate(dueDateFrom, "YYYY-MM-DD") : undefined,
            evaluationEndFilter: dueDateTo && dueDateTo instanceof Date ? Utils.formatDate(dueDateTo, "YYYY-MM-DD") : undefined,
            
            // Status filters
            statusFilter: statusFilter || undefined,
            
            // Entity filters (join multiple values with commas for OR logic)
            studentFilter: students.length > 0 ? students.map(this.getValue).join(",") : undefined,
            courseFilter: courses.length > 0 ? courses.map(this.getValue).join(",") : undefined,
            committeeFilter: committees.length > 0 ? committees.map(this.getValue).join(",") : undefined,
            labelsFilter: labels.length > 0 ? labels.map(this.getValue).join(",") : undefined,
            assigneeFilter: assignees.length > 0 ? assignees.map(this.getValue).join(",") : undefined,
            creatorFilter: creators.length > 0 ? creators.map(this.getValue).join(",") : undefined,
            dashboardFilter: dashboards.length > 0 ? dashboards.map(this.getValue).join(",") : undefined,
            strategicPlanFilter: strategicPlanItems.length > 0 ? strategicPlanItems.map(this.getValue).join(",") : undefined,
            
            // Text search (join keywords for search query)
            queryFilter: taskSummaryKeywords.length > 0 ? taskSummaryKeywords.map(this.getValue).join(" ") : undefined,
            
            // Include flags
            includeObservations,
            includeOpenTasks,
            includeClosedGoalMetTasks,
            includeClosedGoalNotMetTasks
        };

        // Update state with current filters and trigger search when state is updated
        this.setState({ currentFilters: filters }, () => {
            // scroll search results into view
            const lists = document.getElementById("selectionLists");
            if(lists) lists.scrollIntoView();

            this.onNewSearch();
        });
    };

    clearForm = () => {
        this.setState({
            dateCreatedFrom: undefined,
            dateCreatedTo: undefined,
            dueDateFrom: undefined,
            dueDateTo: undefined,
            includeOpenTasks: false,
            includeClosedGoalMetTasks: false,
            includeClosedGoalNotMetTasks: false,
            includeObservations: false,
            strategicPlanItems: [],
            courses: [],
            students: [],
            committees: [],
            assignees: [],
            labels: [],
            dashboards: [],
            creators: [],
            taskSummaryKeywords: [],
            currentFilters: undefined,
        });
    };

    cancelSearch = () => {
        this.clearForm();

        this.setState({
            redirectToTickets: true,
        });
    };

    getStrategicPlanOptionStyles = (styles, {data}) => ({
        ...styles,
        fontWeight: !data.parent_id ? "bold" : "default",
        marginLeft: data.level === "second" ? "10px" :
            data.level === "third" ? "20px" :
            data.level === "fourth" ? "30px" : "default"
    });

    getStrategicPlanItems = () => {
        const {strategicPlanStore} = this.props;

        if(strategicPlanStore){
            if(!strategicPlanStore.individualItems) {return [];}
            else {
                if(strategicPlanStore.totalCountStrategicPlans > 1)
                    {return strategicPlanStore.individualItems?.map
                        (x => ({value: x.id, label: `${ x.name} - ${ x.strategic_plan_name }`, parent_id: x.parent_id, level: x.level}));}
                else
                    {return strategicPlanStore.individualItems?.map
                        (x => ({value: x.id, label: x.name, parent_id: x.parent_id, level: x.level}));}
            }
        }
    };

    getCommittees = () => {
        const {commonStore} = this.props;

        if(commonStore){
            if(!commonStore.committees) return [];
            return commonStore.committees.map(x => ({value: x.id, label: x.name}));
        }
    };

    getStudents = () => {
        const {commonStore} = this.props;

        if(commonStore){
            if(!commonStore.students) return [];
            return commonStore.students.map(s => ({value: s.ruuid, label: Utils.studentToStudentLabel(s)}));
        }
    };

    getCourses = () => {
        const {commonStore} = this.props;
        if(commonStore){
            if(!commonStore.courses) return [];
            return commonStore.courses.map(x => ({value: x.id, label: x.name}));
        }
    };

    getDashboards = () => {
        const {dashboardStore} = this.props;
        if(dashboardStore){
            if(!dashboardStore.dashboards) return [];
            return dashboardStore.dashboards.map(x => ({value: x.id, label: x.name}));
        }
    };

    getLabels = () => {
        const {labelStore} = this.props;
        if(labelStore){
            if(!labelStore.labels) return [];
            return labelStore.labels.map(x => ({value: x.id, label: x.name}));
        }
    };

    getUsers = () => {
        const {userStore} = this.props;
        if(userStore){
            if(!userStore.users) return [];
            return userStore.users.map(x => ({
                value: x.ruuid,
                label: `${ x.prefix } ${ x.first_name } ${ x.last_name } ${ x.suffix }`,
            }));
        }
    };

    handleDateCreatedFromChange = (date) => {
        const createdFrom = new Date(date);
        createdFrom.setHours(0, 0, 0, 0); // Set to start of day with milliseconds
        this.setState({dateCreatedFrom: date ? createdFrom : date});
    };

    handleDateCreatedToChange = (date) => {
        const createdTo = new Date(date);
        createdTo.setHours(23, 59, 59, 999); // Set to end of day with milliseconds
        this.setState({dateCreatedTo: date ? createdTo : date});
    };

    handleDueDateFromChange = (date) => {
        const fromChange = new Date(date);
        fromChange.setHours(0, 0, 0, 0); // Set to start of day with milliseconds
        this.setState({dueDateFrom: date ? fromChange : date});
    };

    handleDueDateToChange = (date) => {
        const toChange = new Date(date);
        toChange.setHours(23, 59, 59, 999); // Set to end of day with milliseconds
        this.setState({dueDateTo: date ? toChange : date});
    };

    handleStrategicPlanChange = (strategicPlanItems) => {
        this.setState({strategicPlanItems: strategicPlanItems || []});
    };

    handleCoursesChange = (courses) => {
        this.setState({courses: courses || []});
    };

    handleCommitteesChange = (committees) => {
        this.setState({committees: committees || []});
    };

    handleStudentsChange = (students) => {
        this.setState({students: students || []});
    };

    handleAssigneesChange = (assignees) => {
        this.setState({assignees: assignees || []});
    };

    handleCreatorsChange = (creators) => {
        this.setState({creators: creators || []});
    };

    handleLabelsChange = (labels) => {
        this.setState({labels: labels || []});
    };

    handleDashboardsChange = (dashboards) => {
        this.setState({dashboards: dashboards || []});
    };

    handleIncludeOpenTasksChange = () => this.setState({includeOpenTasks: !this.state.includeOpenTasks});

    handleIncludeGoalMetTaskChange = () => this.setState({includeClosedGoalMetTasks: !this.state.includeClosedGoalMetTasks});

    handleIncludeGoalNotMetTaskChange = () => this.setState({includeClosedGoalNotMetTasks: !this.state.includeClosedGoalNotMetTasks});

    handleIncludeObservationsChange = () => this.setState({includeObservations: !this.state.includeObservations});

    handleSummaryKeywordCreate = (x: string) => `Add Summary Keyword "${ x }"`;

    renderForm(){
        const {commonStore, dashboardStore, labelStore, userStore} = this.props;
        if(!commonStore || !dashboardStore || !labelStore || !userStore) return null;
        return (
            <div style={ {marginTop: 20} }>
                <div className="report-form edit">
                    <div className="header edit">
                        <span>Create Report</span>
                    </div>
                    <div className="body">
                        <form>
                            <div className="row no-margin">
                                <div className="col-md-4">
                                    <h5>Settings</h5>
                                    <Row>
                                        <Form.Group as={Col}>
                                            <Form.Label>Date Created From</Form.Label>
                                            <DatePicker
                                                placeholderText="Date Created From"
                                                dateFormat="MM/dd/yyyy"
                                                maxDate={ this.state.dateCreatedTo ? this.state.dateCreatedTo : TODAY}
                                                selected={ this.state.dateCreatedFrom }
                                                className="form-control"
                                                onChange={ this.handleDateCreatedFromChange }/>
                                        </Form.Group>
                                        <Form.Group as={Col}>
                                            <Form.Label>Date Created To</Form.Label>
                                            <DatePicker
                                                placeholderText="Date Created To"
                                                dateFormat="MM/dd/yyyy"
                                                maxDate={ TODAY }
                                                selected={ this.state.dateCreatedTo }
                                                className="form-control"
                                                onChange={ this.handleDateCreatedToChange }/>
                                        </Form.Group>
                                    </Row>
                                    <Row>
                                        <Form.Group as={Col}>
                                            <Form.Label>Due Date From</Form.Label>
                                            <DatePicker
                                                placeholderText="Due Date From"
                                                dateFormat="MM/dd/yyyy"
                                                maxDate={this.state.dueDateTo}
                                                selected={ this.state.dueDateFrom }
                                                className="form-control"
                                                onChange={ this.handleDueDateFromChange }/>
                                        </Form.Group>
                                        <Form.Group as={Col}>
                                            <Form.Label>Due Date To</Form.Label>
                                            <DatePicker
                                                placeholderText="Due Date To"
                                                dateFormat="MM/dd/yyyy"
                                                selected={ this.state.dueDateTo }
                                                className="form-control"
                                                onChange={ this.handleDueDateToChange }/>
                                        </Form.Group>
                                    </Row>

                                    <h5>&nbsp;</h5>
                                    <h5>ActionPlans to Include</h5>
                                    <div>
                                        <SimpleCheckbox
                                            isChecked={this.state.includeOpenTasks}
                                            label="Open"
                                            onChange={this.handleIncludeOpenTasksChange}
                                            id={"include-open-tasks"} />
                                    </div>
                                    <div>
                                        <SimpleCheckbox
                                            isChecked={this.state.includeClosedGoalMetTasks}
                                            label={"Closed/Goal Met"}
                                            onChange={this.handleIncludeGoalMetTaskChange}
                                            id={"include-closed-met-tasks"} />
                                    </div>
                                    <div>
                                        <SimpleCheckbox
                                            isChecked={this.state.includeClosedGoalNotMetTasks}
                                            label="Closed/Goal Not Met"
                                            onChange={ this.handleIncludeGoalNotMetTaskChange }
                                            id={"include-closed-goal-not-met-tasks"} />
                                    </div>
                                    <div>
                                        <SimpleCheckbox
                                            isChecked={this.state.includeObservations}
                                            label="Non-Actionable"
                                            onChange={ this.handleIncludeObservationsChange }
                                            id={"include-observations"} />
                                    </div>
                                </div>

                                <div className="col-md-8">
                                    <h5>Filters</h5>

                                    <label>Institutional Effectiveness Plan</label>
                                    <SelectWrapper
                                        closeMenuOnSelect={false}
                                        isMulti={true}
                                        onChange={this.handleStrategicPlanChange}
                                        options={this.getStrategicPlanItems()}
                                        hideSelectedOptions={true}
                                        value={this.state.strategicPlanItems}
                                        styles={{
                                            option: this.getStrategicPlanOptionStyles
                                        }}
                                    />

                                    <div className="row">
                                        <div className="col-md-6">
                                            <label>Courses</label>
                                            <SelectWrapper
                                                closeMenuOnSelect={false}
                                                isMulti={true}
                                                onChange={this.handleCoursesChange}
                                                options={this.getCourses()}
                                                placeholder="Courses"
                                                hideSelectedOptions={true}
                                                value={this.state.courses}
                                            />

                                            <label>Students</label>
                                            <SelectWrapper
                                                closeMenuOnSelect={ false }
                                                isMulti={ true }
                                                onChange={ this.handleStudentsChange }
                                                options={ this.getStudents() }
                                                placeholder="Students"
                                                hideSelectedOptions={ true }
                                                value={ this.state.students }
                                            />

                                            <label>Assignees</label>
                                            <SelectWrapper
                                                closeMenuOnSelect={false}
                                                isMulti={true}
                                                onChange={this.handleAssigneesChange}
                                                options={this.getUsers()}
                                                placeholder="Assignees"
                                                hideSelectedOptions={true}
                                                value={this.state.assignees}
                                            />

                                            <label>Creators</label>
                                            <SelectWrapper
                                                closeMenuOnSelect={false}
                                                isMulti={true}
                                                onChange={this.handleCreatorsChange}
                                                options={this.getUsers()}
                                                placeholder="Creators"
                                                hideSelectedOptions={true}
                                                value={this.state.creators}
                                            />

                                        </div>

                                        <div className="col-md-6">
                                            <label>Labels</label>
                                            <SelectWrapper
                                                closeMenuOnSelect={false}
                                                isMulti={true}
                                                onChange={this.handleLabelsChange}
                                                options={this.getLabels()}
                                                placeholder="Labels"
                                                hideSelectedOptions={true}
                                                value={this.state.labels}
                                            />

                                            <label>Committees</label>
                                            <SelectWrapper
                                                closeMenuOnSelect={false}
                                                isMulti={true}
                                                onChange={this.handleCommitteesChange}
                                                options={this.getCommittees()}
                                                placeholder="Committees"
                                                hideSelectedOptions={true}
                                                value={this.state.committees}
                                            />

                                            <label>Dashboards</label>
                                            <SelectWrapper
                                                closeMenuOnSelect={false}
                                                isMulti={true}
                                                onChange={this.handleDashboardsChange}
                                                options={this.getDashboards()}
                                                placeholder="Dashboards"
                                                hideSelectedOptions={true}
                                                value={this.state.dashboards}
                                            />

                                            <label>Summary Keywords</label>
                                            <CreatableWrapper
                                                isMulti={true}
                                                onChange={this.handleSummaryKeyword}
                                                placeholder="Summary Keywords"
                                                value={this.state.taskSummaryKeywords}
                                                className="keywords"
                                                formatCreateLabel={this.handleSummaryKeywordCreate}
                                            />

                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Row className="no-margin mt-2">
                                <Col className="form-actions">
                                    <Button
                                        variant="secondary"
                                        size="lg"
                                        id="ticket-form-clear"
                                        onClick={ this.clearForm }>
                                        Clear Form
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="lg"
                                        id="ticket-form-cancel"
                                        onClick={ this.cancelSearch }>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        id="ticket-form-search"
                                        onClick={ this.handleTicketSearch }>
                                        Search
                                    </Button>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </div>
                <TicketSelector 
                    ticketStore={ this.props.ticketStore } 
                    reportStore={ this.props.reportStore }
                    searchFilters={ this.state.currentFilters }
                    onNewSearch={ (onNewSearch) => this.onNewSearch = onNewSearch }/>
            </div>
        );
    }
}

export function TicketSearchForm(props: Omit<ITicketSearchFormModalProps, "navigate">) {
    const navigate = useNavigate();
    return <TicketSearchFormClass {...props} navigate={navigate} />;
}
